import React, { useEffect } from "react";
import {
  getMarketingRouteHero,
  getMarketingRouteKey,
  marketingRouteLoaders,
  type MarketingRouteKey,
} from "./marketingRouteLoaders";

const preloadedRoutes = new Set<MarketingRouteKey>();
const preloadedImages = new Set<string>();

const preloadRoute = (routeKey: MarketingRouteKey): void => {
  if (preloadedRoutes.has(routeKey)) return;
  preloadedRoutes.add(routeKey);
  void marketingRouteLoaders[routeKey]().catch(() => {
    preloadedRoutes.delete(routeKey);
  });
};

const preloadImage = (src: string): void => {
  if (preloadedImages.has(src)) return;
  preloadedImages.add(src);
  const image = new Image();
  image.src = src;
};

const getInternalAnchorPathname = (event: Event): string | undefined => {
  const target = event.target;
  if (!(target instanceof Element)) return undefined;
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (!anchor) return undefined;

  const url = new URL(anchor.href, window.location.href);
  return url.origin === window.location.origin ? url.pathname : undefined;
};

const MarketingRoutePreloader: React.FC = () => {
  useEffect(() => {
    const handleNavigationIntent = (event: Event): void => {
      const pathname = getInternalAnchorPathname(event);
      if (!pathname || pathname === window.location.pathname) return;

      const routeKey = getMarketingRouteKey(pathname);
      if (!routeKey) return;
      preloadRoute(routeKey);

      const hero = getMarketingRouteHero(pathname);
      if (hero) preloadImage(hero);
    };

    document.addEventListener("pointerover", handleNavigationIntent, { passive: true });
    document.addEventListener("focusin", handleNavigationIntent);
    document.addEventListener("touchstart", handleNavigationIntent, { passive: true });
    return () => {
      document.removeEventListener("pointerover", handleNavigationIntent);
      document.removeEventListener("focusin", handleNavigationIntent);
      document.removeEventListener("touchstart", handleNavigationIntent);
    };
  }, []);

  return null;
};

export default MarketingRoutePreloader;
